"use client";

import { Rarity, TraitCategory } from "@/types/cat";
import { SearchInput } from "./SearchInput";
import { RarityFilter } from "./RarityFilter";
import { TraitFilter } from "./TraitFilter";
import { X, SlidersHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

interface FilterBarProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  selectedRarities: Rarity[];
  onRaritiesChange: (rarities: Rarity[]) => void;
  selectedTraits: Record<string, string[]>;
  onTraitsChange: (traits: Record<string, string[]>) => void;
  traits: TraitCategory[];
  resultCount: number;
  sortBy: string;
  onSortChange: (sort: string) => void;
}

const sortOptions = [
  { value: "id-asc", label: "ID ascending" },
  { value: "id-desc", label: "ID descending" },
  { value: "rarity-desc", label: "Rarity" },
  { value: "luck-desc", label: "Highest Luck" },
];

export function FilterBar({
  searchValue,
  onSearchChange,
  selectedRarities,
  onRaritiesChange,
  selectedTraits,
  onTraitsChange,
  traits,
  resultCount,
  sortBy,
  onSortChange,
}: FilterBarProps) {
  const hasFilters =
    searchValue ||
    selectedRarities.length > 0 ||
    Object.values(selectedTraits).some((arr) => arr.length > 0);

  const clearFilters = () => {
    onSearchChange("");
    onRaritiesChange([]);
    onTraitsChange({});
  };

  const FilterContent = () => (
    <div className="space-y-6">
      <RarityFilter
        selected={selectedRarities}
        onChange={onRaritiesChange}
      />
      <TraitFilter
        traits={traits}
        selected={selectedTraits}
        onChange={onTraitsChange}
      />
      {hasFilters && (
        <Button
          variant="outline"
          className="w-full"
          onClick={clearFilters}
        >
          <X className="mr-2 h-4 w-4" />
          Clear filters
        </Button>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-1 flex-col gap-3 md:flex-row md:items-center">
          <div className="flex-1 md:max-w-sm">
            <SearchInput value={searchValue} onChange={onSearchChange} />
          </div>
          <select
            value={sortBy}
            onChange={(e) => onSortChange(e.target.value)}
            className="h-9 rounded-md border border-border bg-transparent px-3 text-sm"
          >
            {sortOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">{resultCount}</span> cats
          </p>
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="outline" className="md:hidden">
                <SlidersHorizontal className="mr-2 h-4 w-4" />
                Filter
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="border-border bg-card">
              <SheetHeader>
                <SheetTitle className="font-pixel text-sm">Filter</SheetTitle>
              </SheetHeader>
              <div className="mt-6">
                <FilterContent />
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* Active filter chips */}
      {hasFilters && (
        <div className="flex flex-wrap items-center gap-2">
          {searchValue && (
            <button
              onClick={() => onSearchChange("")}
              className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-xs text-primary"
            >
              Search: {searchValue}
              <X className="h-3 w-3" />
            </button>
          )}
          {selectedRarities.map((rarity) => (
            <button
              key={rarity}
              onClick={() =>
                onRaritiesChange(selectedRarities.filter((r) => r !== rarity))
              }
              className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-xs text-primary"
            >
              {rarity}
              <X className="h-3 w-3" />
            </button>
          ))}
          {Object.entries(selectedTraits).map(([key, values]) =>
            values.map((value) => (
              <button
                key={`${key}-${value}`}
                onClick={() =>
                  onTraitsChange({
                    ...selectedTraits,
                    [key]: values.filter((v) => v !== value),
                  })
                }
                className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-xs text-primary"
              >
                {value}
                <X className="h-3 w-3" />
              </button>
            ))
          )}
          <button
            onClick={clearFilters}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            Clear all
          </button>
        </div>
      )}
    </div>
  );
}
